import { useEffect, useRef } from 'react';

import type { PaymentIntent } from '../types/api';
import { isTerminalStatus } from './uiState';

/**
 * Cuando el comercio abre el checkout en una VENTANA propia (ESMAX lo hace), al terminar el cobro:
 * se le avisa a esa pagina y la ventana se cierra sola.
 *
 * <p>Pedido por el integrador y decidido el 2026-10-28. Antes el checkout se quedaba en su pantalla
 * final y el comprador tenia que cerrarlo; el sistema viejo lo cerraba. Funciona porque `/session`
 * se sirve con `Cross-Origin-Opener-Policy: unsafe-none` (nginx.conf): con la politica anterior el
 * navegador cortaba la relacion entre las dos ventanas y `window.opener` llegaba nulo.
 *
 * <p>Tres limites, a proposito:
 *
 * <ul>
 *   <li><strong>Solo si alguien abrio esta ventana.</strong> Sin `window.opener` —el comprador llego
 *       por un enlace o una redireccion de pagina completa— no hay a quien avisar ni ventana que
 *       cerrar: cerrar la pestana de alguien seria hostil. Empotrado (iframe) tampoco: ahi el aviso
 *       ya va por el puente de postMessage del SDK.
 *   <li><strong>El aviso solo va a los origenes del comercio</strong> (`allowed_origins`), nunca a
 *       `*`: es el mismo criterio que ya decide quien puede empotrar el checkout.
 *   <li><strong>Lo oficial sigue siendo el webhook y la consulta.</strong> El aviso es una cortesia
 *       para cerrar el modal o refrescar la pagina; no lleva nada que el comercio deba creerse sin
 *       consultar.
 * </ul>
 */
export const SEGUNDOS_ANTES_DE_CERRAR = 3;

/** Tipo del mensaje que recibe la pagina del comercio. */
export const AVISO_CHECKOUT_TERMINADO = 'apipay.checkout.completed';

export interface AvisoCheckoutTerminado {
  type: typeof AVISO_CHECKOUT_TERMINADO;
  /** `pi_...` del cobro. */
  payment_intent: string | null;
  /**
   * Estado del intent en minusculas: succeeded, failed, canceled, expired o reversed; o
   * `pending_authorization` cuando el cobro espera la autorizacion de una empresa en el banco y el
   * desenlace llegara despues, por webhook. Es el mismo nombre que el evento
   * `payment_intent.pending_authorization`.
   */
  status: string;
}

export interface UseAvisoYCierreArgs {
  intent: PaymentIntent | null;
  /** El comprador se fue con «Cancelar» y el cobro no llego a cerrarse en el servidor. */
  canceladoAqui: boolean;
  /** `false` si el checkout vive en un iframe: entonces no se hace nada de esto. */
  esVentanaPrincipal: boolean;
}

export function useAvisoYCierreDeVentana({
  intent,
  canceladoAqui,
  esVentanaPrincipal,
}: UseAvisoYCierreArgs): void {
  const yaAvisado = useRef(false);
  const temporizador = useRef<number | null>(null);
  const origenesConocidos = useRef<readonly string[]>([]);

  // El cierre se cancela SOLO al desmontar. Si colgara de las dependencias del efecto de abajo, la
  // siguiente lectura del intent (el sondeo devuelve un objeto nuevo) lo cancelaria sin rearmarlo.
  useEffect(
    () => (): void => {
      if (temporizador.current !== null) {
        window.clearTimeout(temporizador.current);
      }
    },
    [],
  );

  // PENDING_AUTHORIZATION no es terminal, pero para el COMPRADOR si es un final: ya hizo su parte
  // y lo que falta (la firma de su empresa) no pasa en esta ventana.
  const estado =
    intent !== null &&
    (isTerminalStatus(intent.status) || intent.status === 'PENDING_AUTHORIZATION')
      ? intent.status.toLowerCase()
      : canceladoAqui
        ? 'canceled'
        : null;
  const intentId = intent?.id ?? null;
  const origenes = intent?.checkout.allowed_origins;

  // LOS ORIGENES SE RECUERDAN, porque justo al terminar desaparecen: la API omite el bloque
  // `checkout` de un intent TERMINAL (PaymentIntentController), asi que la lectura que trae el
  // SUCCEEDED llega con `allowed_origins` vacio. Usando esa, el aviso no se mandaba a nadie y la
  // ventana se cerraba igual: lo destapo ESMAX el 2026-10-29 con un listener sin filtro que no
  // recibio nada. Va antes del efecto del aviso para que ese ya lo encuentre.
  useEffect(() => {
    if (origenes !== undefined && origenes.length > 0) {
      origenesConocidos.current = origenes;
    }
  }, [origenes]);

  useEffect(() => {
    if (!esVentanaPrincipal || estado === null || yaAvisado.current) {
      return undefined;
    }
    const opener = window.opener as Window | null;
    if (opener === null || opener === window) {
      return undefined;
    }
    yaAvisado.current = true;
    temporizador.current = avisarYProgramarCierre(
      opener,
      intentId,
      estado,
      origenesConocidos.current,
    );
    return undefined;
  }, [esVentanaPrincipal, estado, intentId]);
}

/**
 * Le avisa a la pagina que abrio esta ventana y programa el cierre. Devuelve el temporizador, o
 * `null` si no hay quien la abriera (entonces no hace nada).
 */
function avisarYProgramarCierre(
  opener: Window | null,
  intentId: string | null,
  estado: string,
  origenes: readonly string[] | undefined,
): number | null {
  if (opener === null || opener === window) {
    return null;
  }
  const aviso: AvisoCheckoutTerminado = {
    type: AVISO_CHECKOUT_TERMINADO,
    payment_intent: intentId,
    status: estado,
  };
  for (const origen of origenesValidos(origenes)) {
    try {
      opener.postMessage(aviso, origen);
    } catch {
      // La pagina de origen ya no esta o no es de ese origen: el aviso es una cortesia.
    }
  }
  // Se deja leer el desenlace un momento antes de cerrar.
  return window.setTimeout(() => {
    window.close();
  }, SEGUNDOS_ANTES_DE_CERRAR * 1000);
}

/** Solo origenes http(s) bien formados: `postMessage` lanza con uno invalido. */
function origenesValidos(origenes: readonly string[] | undefined): string[] {
  const validos: string[] = [];
  for (const candidato of origenes ?? []) {
    try {
      const url = new URL(candidato);
      if (url.protocol === 'https:' || url.protocol === 'http:') {
        validos.push(url.origin);
      }
    } catch {
      // Se ignora: un origen mal escrito en la configuracion del comercio no rompe el cierre.
    }
  }
  return validos;
}
